// frontend/src/hooks/useInactivityTimeout.js
import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLogoutMutation } from '../slices/usersApiSlice';
import { logout } from '../slices/authSlice';

/**
 * Custom hook to handle user inactivity timeout
 * @param {Object} options - Configuration options
 * @param {number} options.timeoutMinutes - Minutes of inactivity before logout (default: 30)
 * @param {number} options.warningMinutes - Minutes before timeout to show warning (default: 5)
 * @param {function} options.onWarning - Callback when warning is triggered
 * @param {function} options.onLogout - Callback when auto-logout occurs
 */
const useInactivityTimeout = ({
                                  timeoutMinutes = 30,
                                  warningMinutes = 5,
                                  onWarning = null,
                                  onLogout = null
                              } = {}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [logoutApiCall] = useLogoutMutation();
    const { userInfo } = useSelector((state) => state.auth);

    const timeoutRef = useRef(null);
    const warningRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    // Convert minutes to milliseconds
    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;

    // Perform logout
    const performLogout = useCallback(async () => {
        try {
            await logoutApiCall().unwrap();
            dispatch(logout());
            navigate('/login');

            if (onLogout) {
                onLogout();
            }
        } catch (err) {
            console.error('Auto-logout failed:', err);
            // Force logout on frontend even if API fails
            dispatch(logout());
            navigate('/login');
        }
    }, [dispatch, navigate, logoutApiCall, onLogout]);

    // Reset all timers
    const resetTimers = useCallback(() => {
        // Clear existing timers
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (warningRef.current) {
            clearTimeout(warningRef.current);
        }

        // Update last activity time
        lastActivityRef.current = Date.now();
        localStorage.setItem('lastActivity', lastActivityRef.current.toString());

        // Set warning timer
        warningRef.current = setTimeout(() => {
            if (onWarning) {
                onWarning();
            }
        }, warningMs);

        // Set logout timer
        timeoutRef.current = setTimeout(() => {
            performLogout();
        }, timeoutMs);
    }, [timeoutMs, warningMs, onWarning, performLogout]);

    // Track user activity
    const handleActivity = useCallback(() => {
        // Only reset if user is logged in
        if (userInfo) {
            resetTimers();
        }
    }, [userInfo, resetTimers]);

    // Setup activity listeners
    useEffect(() => {
        // Only activate if user is logged in
        if (!userInfo) {
            return;
        }

        // Events to track for activity
        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click'
        ];

        // Throttle activity tracking (don't reset timer on every single event)
        let throttleTimeout = null;
        const throttledHandleActivity = () => {
            if (!throttleTimeout) {
                throttleTimeout = setTimeout(() => {
                    handleActivity();
                    throttleTimeout = null;
                }, 1000); // Throttle to once per second
            }
        };

        // Add event listeners
        events.forEach((event) => {
            window.addEventListener(event, throttledHandleActivity);
        });

        // Check last activity on mount (for page refresh)
        const lastActivity = localStorage.getItem('lastActivity');
        if (lastActivity) {
            const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);

            if (timeSinceLastActivity >= timeoutMs) {
                // Auto-logout if timeout has passed
                performLogout();
                return;
            } else if (timeSinceLastActivity >= warningMs) {
                // Show warning if in warning period
                if (onWarning) {
                    onWarning();
                }
            }
        }

        // Initialize timers
        resetTimers();

        // Cleanup
        return () => {
            events.forEach((event) => {
                window.removeEventListener(event, throttledHandleActivity);
            });
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (warningRef.current) {
                clearTimeout(warningRef.current);
            }
            if (throttleTimeout) {
                clearTimeout(throttleTimeout);
            }
        };
    }, [userInfo, resetTimers, performLogout, timeoutMs, warningMs, onWarning]);

    // Return method to manually extend session (for "Stay Logged In" button)
    return {
        extendSession: handleActivity,
        performLogout
    };
};

export default useInactivityTimeout;